/**
 * Formatting utilities — dates, display strings.
 */

import { format, formatDistanceToNow, isToday, isYesterday } from 'date-fns';

/**
 * Formats a date for display in the CMS.
 * Shows relative time for recent dates, full date otherwise.
 * e.g. "2 hours ago", "Yesterday at 3:45 PM", "14 Jul 2026 at 3:45 PM"
 */
export function formatDisplayDate(date: Date | null | undefined): string {
  if (!date) return '—';

  if (isToday(date)) {
    return `Today at ${format(date, 'h:mm a')}`;
  }
  if (isYesterday(date)) {
    return `Yesterday at ${format(date, 'h:mm a')}`;
  }
  // Within 7 days — show relative
  const diffMs = Date.now() - date.getTime();
  if (diffMs < 7 * 24 * 60 * 60 * 1000) {
    return formatDistanceToNow(date, { addSuffix: true });
  }
  return format(date, 'd MMM yyyy \'at\' h:mm a');
}

/**
 * Formats a date as a short date string.
 * e.g. "14 Jul 2026"
 */
export function formatShortDate(date: Date | null | undefined): string {
  if (!date) return '—';
  return format(date, 'd MMM yyyy');
}

/**
 * Formats a date as ISO for Firestore queries.
 */
export function formatIsoDate(date: Date): string {
  return date.toISOString();
}

/**
 * Capitalises the first letter of a string.
 */
export function capitalise(str: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Converts a snake_case string to a readable label.
 * e.g. "not_started" → "Not Started"
 */
export function snakeToLabel(str: string): string {
  return str.split('_').map(capitalise).join(' ');
}

/**
 * Truncates a string to a max length, appending "…" if truncated.
 */
export function truncate(str: string | null | undefined, maxLength: number): string {
  if (!str) return '';
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - 1) + '…';
}

/**
 * Returns a display name from a user object, falling back to email.
 */
export function getUserDisplayName(user: { displayName?: string | null; email?: string | null } | null): string {
  if (!user) return 'Unknown User';
  return user.displayName || user.email || 'Unknown User';
}
